import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { createUser } from "./utils/userManagementApi";
import logger from "./utils/logger";
import "./AddUserModal.css";

const AddUserModal = ({ onClose, onUserCreated, existingUsers = [] }) => {
  const { getAccessToken } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    role: "Site Admin",
  });

  const [countryCode, setCountryCode] = useState("+1");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Common country codes with flags
  const countries = [
    { code: "+1", name: "US/Canada", flag: "🇺🇸" },
    { code: "+44", name: "UK", flag: "🇬🇧" },
    { code: "+61", name: "Australia", flag: "🇦🇺" },
    { code: "+81", name: "Japan", flag: "🇯🇵" },
    { code: "+86", name: "China", flag: "🇨🇳" },
    { code: "+33", name: "France", flag: "🇫🇷" },
    { code: "+49", name: "Germany", flag: "🇩🇪" },
    { code: "+39", name: "Italy", flag: "🇮🇹" },
    { code: "+34", name: "Spain", flag: "🇪🇸" },
    { code: "+91", name: "India", flag: "🇮🇳" },
    { code: "+52", name: "Mexico", flag: "🇲🇽" },
    { code: "+55", name: "Brazil", flag: "🇧🇷" },
    { code: "+7", name: "Russia", flag: "🇷🇺" },
    { code: "+82", name: "South Korea", flag: "🇰🇷" },
    { code: "+971", name: "UAE", flag: "🇦🇪" },
  ];

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, loading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const digits = value.replace(/\D/g, "");
    setPhoneDigits(digits);

    // Update the full phone number in E.164 format
    if (digits) {
      setFormData((prev) => ({ ...prev, phoneNumber: countryCode + digits }));
    } else {
      setFormData((prev) => ({ ...prev, phoneNumber: "" }));
    }
  };

  const handleCountryChange = (e) => {
    const newCode = e.target.value;
    setCountryCode(newCode);

    // Update the full phone number with new country code
    if (phoneDigits) {
      setFormData((prev) => ({ ...prev, phoneNumber: newCode + phoneDigits }));
    }
  };

  const formatPhoneDisplay = (digits) => {
    if (!digits) return "";

    // Format based on country code
    if (countryCode === "+1" && digits.length === 10) {
      // US/Canada format: (555) 555-5555
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    // Default: add spaces every 3-4 digits
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 10)
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(
      6,
      10
    )} ${digits.slice(10)}`;
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone) return true; // Optional field
    // E.164 format: +1234567890 (1-15 digits)
    const re = /^\+\d{1,15}$/;
    return re.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError("Email, first name, and last name are required");
      return;
    }

    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Check if user with this email already exists
    const emailLower = formData.email.toLowerCase().trim();
    const existingUser = existingUsers.find(
      (u) => u.email.toLowerCase().trim() === emailLower
    );
    if (existingUser) {
      setError(
        `A user with email "${formData.email}" already exists (${existingUser.firstName} ${existingUser.lastName})`
      );
      return;
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      setError("Phone number must be in E.164 format (e.g., +12025551234)");
      return;
    }

    try {
      setLoading(true);

      // Build user data (exclude empty phoneNumber)
      // IMPORTANT: Normalize email to lowercase to ensure Yonomi Platform compatibility
      const userData = {
        email: formData.email.toLowerCase().trim(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
      };

      if (formData.phoneNumber) {
        userData.phoneNumber = formData.phoneNumber;
      }

      await createUser(userData, getAccessToken);
      logger.info("User created successfully", userData);
      onUserCreated();
    } catch (err) {
      logger.error("Failed to create user", err);
      setError(err.message || "Failed to create user");
      setLoading(false);
    }
  };

  return (
    <div className="add-user-modal-overlay">
      <div className="add-user-modal">
        <h3>Add New User</h3>

        {error && <div className="add-user-message error">{error}</div>}

        <form onSubmit={handleSubmit} className="add-user-form">
          <div className="form-group">
            <label>
              Email<span className="required">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="<user@example.com>"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              First Name<span className="required">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="<John>"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>
              Last Name<span className="required">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="<Doe>"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Phone Number</label>
            <div className="phone-input-container">
              <select
                className="country-code-select"
                value={countryCode}
                onChange={handleCountryChange}
                disabled={loading}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.code}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                className="phone-number-input"
                value={formatPhoneDisplay(phoneDigits)}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
                disabled={loading}
              />
            </div>
            <span className="form-helper-text">
              Optional. Enter digits only (will be formatted as{" "}
              {countryCode + phoneDigits || countryCode + "##########"})
            </span>
          </div>

          <div className="form-group">
            <label>
              Role<span className="required">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={loading}
            >
              <option value="Site Admin">Site Admin</option>
              <option value="Organization Admin">Organization Admin</option>
              <option value="Full Admin">Full Admin</option>
            </select>
            <span className="form-helper-text">
              Site Admin: Basic access. Org Admin: Can manage assigned
              organizations. Full Admin: Full system access.
            </span>
          </div>

          <div className="add-user-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;
